import React, { useState } from 'react';
import Input from './BaseInput';
import ChipList from 'shared/buttons/ChipList';
import Button from 'shared/buttons';

const TextInput = ({ value, onChange, onAdd, ...props }) => {
  const handleChange = ({ target: { value } }) => {
    if (value.endsWith(',')) {
      const newItem = value.substring(0, value.length - 1);
      onAdd(newItem);
    } else {
      onChange(value);
    }
  };

  const handleAdd = () => onAdd(value);

  const handleKeyDown = (e) => {
    const key = e.key.toLowerCase();
    const keysToSearch = ['enter', 'tab'];

    if (keysToSearch.includes(key)) {
      e.preventDefault();
      onAdd(value);
    }
  };

  return (
    <Input
      value={value}
      onChange={handleChange}
      quickAddClick={handleAdd}
      onKeyDown={handleKeyDown}
      placeholder='Type and press enter or +'
      {...props}
    />
  );
};

const defaultValue = [];

/**
 * An input with chips list.
 *
 * @typedef {Omit<Omit<React.ComponentPropsWithoutRef<Input>, 'value'>, 'onChange'>} BaseInputProps
 *
 * @typedef {Object} AdditionalInputWithChipsProps
 * @property {Array<string>} [value] A list of values.
 * @property {Function} onChange A callback when value is changed.
 *
 * @param {BaseInputProps & AdditionalInputWithChipsProps} props Component props. Extends BaseInput props.
 * @returns {React.ReactElement} Input with chips list.
 */
const InputWithChips = ({ value = defaultValue, onChange, disabled, ...props }) => {
  const [text, setText] = useState('');

  const onAdd = (item) => {
    setText('');
    if (value.includes(item) || item.trim() === '') {
      return;
    }
    onChange({ value: [...value, item] });
  };

  const onRemoveHandler = disabled ? undefined : (item) => {
    const newValue = value.filter((i) => i !== item);
    onChange({ value: newValue });
  };

  return (
    <div className='input-with-chips'>
      <div className='input-items-container'>
        <TextInput value={text} onChange={setText} onAdd={onAdd} disabled={disabled} {...props} />
        {!(disabled || text.trim() === '') && (
          <Button icon='plus' onClick={() => onAdd(text)} fillMode='flat' themeColor='tertiary' />
        )}
        {(!disabled && value?.length > 1) && (
          <Button
            icon='x'
            tooltip='Reset items'
            disabled={disabled}
            onClick={() => {
              onChange({ value: [] });
            }}
            themeColor='tertiary'
            fillMode='flat'
          />
        )}
      </div>
      <ChipList data={value} onItemRemove={onRemoveHandler} disabled={disabled} />
    </div>
  );
};

export default InputWithChips;
